import { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, CustomSelect } from '@/components';
import { env } from '@/common/config';
import { EyeIcon } from '../../components/Icons';
import { usePermissions } from '@/common/hooks/usePermissions';
import { PERMISSIONS } from '@/common/config/permissions';
import { parseApiError, formatConsentStatus } from '@/common/utils';
import { CRMNotesSection } from './CRMNotesSection';
import { CRMTasksSection } from './CRMTasksSection';
import { CRMActivityTimeline } from './CRMActivityTimeline';
import { CRMAttachmentsSection } from './CRMAttachmentsSection';
import { CRMFollowUpsOverviewTable } from './CRMFollowUpsOverviewTable';
import { CRMLostReasonModal } from './CRMLostReasonModal';
import {
  getDisplayPhone,
  getConversationSearchPhone,
} from '../utils/phoneUtils';
import { useModalClose } from '@/common/hooks';
import {
  getCRMPipelineStages,
  getCRMPipelineBoard,
  moveCRMContactStage,
  createCRMContactNote,
} from '../api';
import { syncPipelineBoardContact } from '../utils/pipelineSync';
import { readAnyContactName } from '../../appointments/utils';
import {
  readFlowMeetLink,
  withMeetLink,
} from '../../whatsapp-flows/utils/meetLink';
import {
  useWhatsAppContactDetails,
  useWhatsAppConversations,
  useCustomWhatsAppMessages,
  useWhatsAppConversationDetail,
  useSendWhatsAppMessage,
  useReactToWhatsAppMessage,
} from '../../whatsapp-chat/hooks/useWhatsAppChat';
import { ChatArea } from '../../whatsapp-chat/components/ChatArea';
import { groupMessagesByDate } from '../../whatsapp-chat/utils/groupMessages';
import toast from 'react-hot-toast';
import heic2any from 'heic2any';
import { CRMFollowUpsSection } from './CRMFollowUpsSection';
import {
  TableChip,
  TABLE_CHIP_BASE,
  DashboardTable,
  DashboardTableHeader,
  DashboardTableBody,
  DashboardTableRow,
  DashboardTableCell,
} from '../../components/PremiumDesign';
import { SourceIcon } from '../../components/SourceIcon';
import { InlineEmployeeSelector } from './InlineEmployeeSelector';
import { InlineTagSelector } from './InlineTagSelector';
import { useFormatters } from '../../hooks/useFormatters';
import { ContactQuotationsList } from './ContactQuotationsList';

function getBoardStages(boardData) {
  if (Array.isArray(boardData)) return boardData;
  if (Array.isArray(boardData?.stages)) return boardData.stages;
  if (Array.isArray(boardData?.results)) return boardData.results;
  return [];
}

function findContactStageFromBoard(boardData, contactId) {
  if (!contactId) return null;
  for (const stage of getBoardStages(boardData)) {
    const contacts = stage.contacts || stage.leads || stage.items || [];
    const found = contacts.some(
      (c) =>
        String(c.id) === String(contactId) ||
        String(c.contact_id) === String(contactId) ||
        String(c.contact?.id) === String(contactId),
    );
    if (found) return stage;
  }
  return null;
}

function matchStageByNameOrId(pipelineStages, value) {
  if (value == null || value === '' || !pipelineStages?.length) return null;
  const asString = String(value).toLowerCase();
  return (
    pipelineStages.find((s) => String(s.id) === String(value)) ||
    pipelineStages.find((s) => String(s.name).toLowerCase() === asString) ||
    pipelineStages.find((s) => String(s.slug).toLowerCase() === asString) ||
    null
  );
}

function resolveContactStageId(
  contactDetails,
  pipelineStages = [],
  boardStage = null,
) {
  if (boardStage?.id != null) return boardStage.id;

  const candidates = [
    contactDetails?.current_stage?.id,
    contactDetails?.current_stage_id,
    contactDetails?.pipeline_stage_id,
    contactDetails?.pipeline_stage?.id,
    contactDetails?.stage_id,
    contactDetails?.stage?.id,
    typeof contactDetails?.current_stage === 'string' ||
    typeof contactDetails?.current_stage === 'number'
      ? contactDetails.current_stage
      : null,
    typeof contactDetails?.stage === 'string' ||
    typeof contactDetails?.stage === 'number'
      ? contactDetails.stage
      : null,
  ];

  for (const candidate of candidates) {
    if (candidate == null || candidate === '') continue;
    const matched = matchStageByNameOrId(pipelineStages, candidate);
    if (matched) return matched.id;
    // If stages aren't loaded yet but we have a numeric/string id, keep it.
    if (
      typeof candidate === 'number' ||
      (typeof candidate === 'string' && /^\d+$/.test(candidate))
    ) {
      return candidate;
    }
  }

  const stageName =
    contactDetails?.current_stage?.name ||
    contactDetails?.pipeline_stage?.name ||
    contactDetails?.stage_name ||
    contactDetails?.stage?.name;
  const byName = matchStageByNameOrId(pipelineStages, stageName);
  return byName?.id ?? '';
}

function formatDate(dateStr) {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

function formatDateTime(dateStr) {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
}

const SESSION_STATUS_TONE = {
  running: 'info',
  completed: 'success',
  expired: 'error',
};

function MediaPreviewModal({ isOpen, onClose, mediaUrl, mimeType }) {
  const modalRef = useModalClose({ isOpen, onClose, isDirty: false });

  if (!isOpen || !mediaUrl) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-in fade-in duration-200">
      <div
        ref={modalRef}
        className="relative max-w-4xl w-full max-h-[90vh] flex flex-col items-center justify-center animate-in zoom-in-95 duration-300"
      >
        <button
          onClick={onClose}
          className="absolute -top-12 right-0 sm:-right-12 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors"
        >
          <svg
            className="w-6 h-6"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>

        {mimeType?.startsWith('image/') ||
        /\.(png|jpe?g|webp|gif)$/i.test(mediaUrl) ? (
          <img
            src={mediaUrl}
            alt="Preview"
            className="max-w-full max-h-[85vh] object-contain rounded-lg shadow-2xl"
          />
        ) : mimeType?.startsWith('video/') ||
          /\.(mp4|webm|mov)$/i.test(mediaUrl) ? (
          <video
            src={mediaUrl}
            controls
            className="max-w-full max-h-[85vh] rounded-lg shadow-2xl bg-black"
          />
        ) : (
          <iframe
            src={mediaUrl}
            className="w-full h-[85vh] rounded-lg bg-white"
          />
        )}
      </div>
    </div>
  );
}

function CRMSessionTab({ companyId, clientId }) {
  const { data, isLoading, error } = useWhatsAppContactDetails(
    companyId,
    clientId,
  );
  const [selectedSessionId, setSelectedSessionId] = useState(null);
  const [previewMedia, setPreviewMedia] = useState(null);

  const getMediaUrl = (file) => {
    if (!file || typeof file !== 'string') return '';
    if (
      file.startsWith('http://') ||
      file.startsWith('https://') ||
      file.startsWith('blob:') ||
      file.startsWith('data:')
    ) {
      return file;
    }
    const base = (env.photoBaseUrl || '').replace(/\/$/, '');
    if (!base) return '';
    return `${base}${file.startsWith('/') ? '' : '/'}${file}`;
  };

  const isImageMediaAnswer = (answer) =>
    typeof answer === 'string' &&
    /^Media message:\s*image$/i.test(answer.trim());

  // Auto-select active or latest session
  useEffect(() => {
    if (data?.sessions && data.sessions.length > 0) {
      const activeSession = data.sessions.find((s) => s.is_active);
      if (activeSession) {
        setSelectedSessionId(activeSession.id);
      } else {
        setSelectedSessionId(data.sessions[0].id);
      }
    }
  }, [data]);

  if (isLoading) {
    const bar = 'bg-[#EBF4EA] animate-pulse rounded';
    return (
      <div className="grid grid-cols-1 lg:grid-cols-[300px_1fr] gap-6 w-full min-w-0">
        {/* LEFT COLUMN */}
        <div className="bg-[#F5F5F5] rounded-[16px] p-4 sm:p-5 flex flex-col gap-4 w-full min-w-0 max-w-full overflow-hidden">
          <div className="p-4 bg-white border border-[#EBF4EA] rounded-[16px] flex items-center justify-between gap-3">
            <div className="flex flex-col gap-1.5">
              <div className={`h-2.5 w-24 ${bar}`} />
              <div className={`h-3.5 w-20 ${bar}`} />
            </div>
            <div className={`h-6 w-16 rounded-lg ${bar}`} />
          </div>
          <div className="flex flex-col gap-1.5">
            <div className={`h-3.5 w-20 ${bar}`} />
            <div className={`h-11 w-full rounded-[10px] ${bar}`} />
          </div>
        </div>

        {/* RIGHT COLUMN */}
        <div className="w-full flex flex-col overflow-hidden">
          <div className="bg-white rounded-[12px] border border-[#EBF4EA] overflow-hidden">
            <div className={`h-12 w-full ${bar} !rounded-none`} />
            {Array.from({ length: 10 }).map((_, i) => (
              <div
                key={i}
                className="h-14 w-full flex items-center gap-4 px-4 border-t border-[#EBF4EA]"
              >
                <div className={`h-6 w-20 rounded-full shrink-0 ${bar}`} />
                <div className={`h-3.5 w-1/2 ${bar}`} />
                <div className={`h-3.5 w-24 ${bar}`} />
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error || !data) {
    const errorDetail =
      error?.response?.data?.detail ||
      error?.response?.data?.message ||
      error?.message ||
      'Failed to load details';
    return (
      <div className="flex flex-col items-center justify-center text-center py-12 px-4 bg-red-500/5 rounded-2xl border border-red-500/10">
        <svg
          className="w-10 h-10 text-red-500/60 mb-2.5"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
          />
        </svg>
        <p className="text-text-primary font-semibold text-sm mb-1">
          Failed to load WhatsApp details
        </p>
        <p className="text-red-400 text-xs font-medium max-w-sm">
          {errorDetail}
        </p>
      </div>
    );
  }

  const { customer, sessions = [] } = data;
  const selectedSession = sessions.find((s) => s.id === selectedSessionId);

  const chatHistoryImageAttachments = (
    Array.isArray(selectedSession?.chat_history)
      ? selectedSession.chat_history
      : []
  )
    .filter((chat) => {
      const type = String(chat?.message_type || '').toLowerCase();
      const hasFile =
        Array.isArray(chat?.attachments) && chat.attachments[0]?.file;
      return hasFile && (type === 'image' || isImageMediaAnswer(chat?.body));
    })
    .map((chat) => chat.attachments[0]);

  let chatImageCursor = 0;

  const sessionQuotations = Array.isArray(selectedSession?.quotations)
    ? selectedSession.quotations
    : [];
  let quotationIndex = 0;

  const qaRows = [];
  if (selectedSession?.flow_state) {
    Object.entries(selectedSession.flow_state).forEach(([stepKey, step]) => {
      // flow_state also carries non-question metadata (last_interaction,
      // state, agent_replied) alongside numbered step entries — skip those.
      if (!step || typeof step !== 'object') return;

      if (step.appointment_date) {
        qaRows.push({
          key: stepKey,
          status: 'completed',
          question: 'Appointment Scheduled',
          answer: withMeetLink(
            `${step.appointment_date} ${step.appointment_time ? 'at ' + step.appointment_time : ''}`.trim(),
            readFlowMeetLink(selectedSession.flow_state),
          ),
          attachments: step.attachments || [],
        });
        return;
      }

      if (!step.question) return;

      const isQuotationStep =
        step.is_quotation_step ||
        String(step.question || '')
          .toLowerCase()
          .includes('quotation');

      if (isQuotationStep && sessionQuotations[quotationIndex]) {
        const cq = sessionQuotations[quotationIndex];
        const hasPdf = Boolean(cq.pdf_url);

        qaRows.push({
          key: stepKey,
          status: 'completed',
          question: step.is_confirmation_step
            ? withMeetLink(
                step.question,
                readFlowMeetLink(selectedSession.flow_state),
              )
            : step.question,
          answer: (
            <div className="flex flex-col gap-1.5 w-full max-w-sm bg-white border border-[#EBF4EA] rounded-xl p-3 shadow-sm mt-1">
              <div className="flex items-center justify-between gap-3">
                <div className="flex flex-col min-w-0">
                  <span className="text-xs font-mono font-semibold text-primary truncate">
                    {cq.quote_number || `Quote #${cq.id}`}
                  </span>
                  <span className="text-sm font-semibold text-[#1F2241] truncate">
                    {cq.quotation_title || 'Quotation'}
                  </span>
                </div>
                <div className="flex flex-col items-end shrink-0">
                  <span className="text-sm font-bold text-[#1F2241]">
                    {cq.currency || 'INR'}{' '}
                    {parseFloat(cq.total_amount || 0).toFixed(2)}
                  </span>
                </div>
              </div>
              {hasPdf && (
                <div className="pt-2.5 mt-1 border-t border-[#EBF4EA] flex items-center justify-start">
                  <a
                    href={getMediaUrl(cq.pdf_url)}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-border-default bg-[#F5F5F5] text-xs font-semibold text-[#1F2241] hover:bg-[#EBF4EA] hover:border-primary/30 transition-colors"
                  >
                    <svg
                      className="w-4 h-4 text-primary"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                      />
                    </svg>
                    View PDF
                  </a>
                </div>
              )}
            </div>
          ),
          isReactNode: true,
          messageType: null,
          attachments: [],
        });
        quotationIndex++;
        return;
      }

      const answer =
        step.option_text ||
        step.value ||
        (step.option_number != null ? String(step.option_number) : '-');
      let stepAttachments = Array.isArray(step.attachments)
        ? step.attachments.filter((att) => att?.file)
        : [];

      const isImageStep =
        String(step.message_type || '').toLowerCase() === 'image' ||
        isImageMediaAnswer(answer);

      // Fallback: pull next image attachment from session chat_history.
      if (isImageStep && stepAttachments.length === 0) {
        const fromHistory = chatHistoryImageAttachments[chatImageCursor];
        if (fromHistory) {
          stepAttachments = [fromHistory];
          chatImageCursor += 1;
        }
      }

      qaRows.push({
        key: stepKey,
        status: 'completed',
        question: step.is_confirmation_step
          ? withMeetLink(
              step.question,
              readFlowMeetLink(selectedSession.flow_state),
            )
          : step.question,
        answer,
        messageType: step.message_type || (isImageStep ? 'image' : null),
        attachments: stepAttachments,
      });
    });
  }
  if (selectedSession?.pending_step) {
    qaRows.push({
      key: 'pending-step',
      status: 'pending',
      question: selectedSession.pending_step.is_confirmation_step
        ? withMeetLink(
            selectedSession.pending_step.question_text || 'Pending Step',
            readFlowMeetLink(selectedSession.flow_state),
          )
        : selectedSession.pending_step.question_text || 'Pending Step',
      answer: 'Awaiting customer reply...',
    });
  }

  // If there are left-over quotations (not matched with a step), append them
  while (quotationIndex < sessionQuotations.length) {
    const cq = sessionQuotations[quotationIndex];
    const hasPdf = Boolean(cq.pdf_url);
    qaRows.push({
      key: `leftover-quotation-${cq.id}`,
      status: 'completed',
      question: 'Quotation Generated',
      answer: (
        <div className="flex flex-col gap-1.5 w-full max-w-sm bg-white border border-[#EBF4EA] rounded-xl p-3 shadow-sm mt-1">
          <div className="flex items-center justify-between gap-3">
            <div className="flex flex-col min-w-0">
              <span className="text-xs font-mono font-semibold text-primary truncate">
                {cq.quote_number || `Quote #${cq.id}`}
              </span>
              <span className="text-sm font-semibold text-[#1F2241] truncate">
                {cq.quotation_title || 'Quotation'}
              </span>
            </div>
            <div className="flex flex-col items-end shrink-0">
              <span className="text-sm font-bold text-[#1F2241]">
                {cq.currency || 'INR'}{' '}
                {parseFloat(cq.total_amount || 0).toFixed(2)}
              </span>
            </div>
          </div>
          {hasPdf && (
            <div className="pt-2.5 mt-1 border-t border-[#EBF4EA] flex items-center justify-start">
              <a
                href={getMediaUrl(cq.pdf_url)}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-border-default bg-[#F5F5F5] text-xs font-semibold text-[#1F2241] hover:bg-[#EBF4EA] hover:border-primary/30 transition-colors"
              >
                <svg
                  className="w-4 h-4 text-primary"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
                View PDF
              </a>
            </div>
          )}
        </div>
      ),
      isReactNode: true,
      messageType: null,
      attachments: [],
    });
    quotationIndex++;
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[300px_1fr] gap-6 w-full min-w-0">
      {/* LEFT COLUMN */}
      <div className="bg-[#F5F5F5] rounded-[16px] p-4 sm:p-5 w-full min-w-0 max-w-full overflow-hidden">
        <div className="flex flex-col gap-4 lg:sticky lg:top-0 w-full min-w-0">
          {customer && (
            <div
              className={`p-4 border rounded-[16px] flex items-center justify-between gap-3 ${
                customer.last_send_by_24_hours
                  ? 'bg-[#EBF4EA] border-[#50AB3D]'
                  : 'bg-red-500/5 border-red-500'
              }`}
            >
              <div className="flex flex-col gap-0.5">
                <span className="text-[10px] text-[#868686] uppercase tracking-wider font-semibold">
                  Whatsapp 24h Window
                </span>
                <span className="text-sm font-bold text-[#1F2241]">
                  {typeof customer.customer_type === 'object'
                    ? customer.customer_type?.name || 'Retail Customer'
                    : customer.customer_type || 'Retail Customer'}
                </span>
              </div>
              {customer.last_send_by_24_hours ? (
                <span className="px-2.5 py-1 bg-white border border-[#50AB3D]/30 text-[#50AB3D] rounded-lg text-xs font-semibold flex items-center gap-1.5 shrink-0">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#50AB3D]" />
                  Active
                </span>
              ) : (
                <span className="px-2.5 py-1 bg-white border border-red-500/30 text-red-500 rounded-lg text-xs font-semibold flex items-center gap-1.5 shrink-0">
                  <span className="w-1.5 h-1.5 rounded-full bg-red-500" />
                  Expired
                </span>
              )}
            </div>
          )}

          {sessions.length === 0 ? (
            <div className="text-center text-[#868686] text-sm py-12 border-2 border-dashed border-[#EBF4EA] rounded-2xl">
              No session history found.
            </div>
          ) : (
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium text-text-secondary">
                Select Session
              </label>
              <CustomSelect
                value={selectedSessionId || ''}
                onChange={(val) => setSelectedSessionId(Number(val))}
                options={sessions.map((s) => ({
                  value: s.id,
                  label: `${s.flow_name || `Session #${s.id}`} (${s.is_active ? 'Active' : s.flow_status || 'Ended'})`,
                }))}
                placeholder="Select session"
                className="w-full h-11"
                textSizeClass="text-sm"
              />
            </div>
          )}

          {selectedSession && (
            <div className="bg-white rounded-[16px] border border-[#EBF4EA] p-4 flex flex-col gap-4">
              <div className="flex items-center justify-between gap-3">
                <span className="text-sm font-bold text-[#1F2241]">
                  Whatsapp Flow Session
                </span>
                <TableChip
                  tone={
                    SESSION_STATUS_TONE[
                      String(selectedSession.flow_status || '').toLowerCase()
                    ] || 'neutral'
                  }
                >
                  {selectedSession.flow_status || '-'}
                </TableChip>
              </div>
              <div className="flex flex-col gap-3">
                <div className="flex flex-col gap-0.5">
                  <span className="text-xs text-[#868686]">Start Date</span>
                  <span className="text-sm font-semibold text-[#1F2241]">
                    {formatDateTime(selectedSession.start_time)}
                  </span>
                </div>
                {!selectedSession.is_active && selectedSession.end_time && (
                  <div className="flex flex-col gap-0.5">
                    <span className="text-xs text-[#868686]">End date</span>
                    <span className="text-sm font-semibold text-[#1F2241]">
                      {formatDateTime(selectedSession.end_time)}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* RIGHT COLUMN */}
      <div className="w-full flex flex-col min-w-0 overflow-hidden">
        <DashboardTable className="table-fixed">
          <DashboardTableHeader>
            <th className="py-3 px-2 sm:px-4 text-left whitespace-nowrap w-[90px] sm:w-[130px] !text-xs sm:!text-[16px]">
              Status
            </th>
            <th className="py-3 px-2 sm:px-4 text-left w-[40%] sm:w-[45%] !text-xs sm:!text-[16px]">
              Questions
            </th>
            <th className="py-3 px-2 sm:px-4 text-left !text-xs sm:!text-[16px]">
              Answer
            </th>
          </DashboardTableHeader>
          <DashboardTableBody>
            {!selectedSession ? (
              <DashboardTableRow className="!cursor-default">
                <td
                  colSpan={3}
                  className="py-10 text-center text-sm text-[#868686] whitespace-normal px-4"
                >
                  Select a session to view details.
                </td>
              </DashboardTableRow>
            ) : qaRows.length > 0 ? (
              qaRows.map((row) => {
                const answerTone = String(row.answer).toLowerCase();
                const hasInlineAttachment =
                  row.answer &&
                  /\.(pdf|png|jpe?g|mp4|docx?|xlsx?)$/i.test(row.answer);

                const isImageAnswer =
                  String(row.messageType || '').toLowerCase() === 'image' ||
                  isImageMediaAnswer(row.answer);

                let imageAttachment =
                  (Array.isArray(row.attachments) &&
                    row.attachments.find((att) => att?.file)) ||
                  null;
                const imagePreviewUrl = imageAttachment?.file
                  ? getMediaUrl(imageAttachment.file)
                  : '';
                const canPreviewImage = Boolean(
                  isImageAnswer && imagePreviewUrl,
                );

                const openPreview = (url, mimeType) => {
                  if (!url) return;
                  // Temporary live verification — remove after QA.
                  console.log('[CRMSession] open MediaPreviewModal', {
                    url,
                    mimeType,
                    photoBaseUrl: env.photoBaseUrl,
                  });
                  setPreviewMedia({ url, mimeType: mimeType || '' });
                };

                return (
                  <DashboardTableRow key={row.key} className="!cursor-default">
                    <td className="py-3 px-2 sm:px-4 align-middle">
                      <TableChip
                        tone={
                          row.status === 'completed' ? 'success' : 'warning'
                        }
                        className="!px-2 !py-0.5 !h-7 sm:!h-[36px] sm:!px-4 !text-xs sm:!text-sm"
                      >
                        {row.status === 'completed' ? 'Completed' : 'Pending'}
                      </TableChip>
                    </td>
                    <DashboardTableCell className="!py-3 !px-2 sm:!px-4 whitespace-pre-line break-words !text-xs sm:!text-base">
                      {row.question}
                    </DashboardTableCell>
                    <td
                      className={`py-3 px-2 sm:px-4 align-middle text-xs sm:text-sm font-medium whitespace-pre-line break-words ${
                        row.status === 'pending'
                          ? 'text-[#868686] italic'
                          : row.isReactNode
                            ? ''
                            : answerTone === 'yes'
                              ? 'text-[#50AB3D]'
                              : answerTone === 'no'
                                ? 'text-red-500'
                                : 'text-[#1F2241]'
                      }`}
                    >
                      <div className="flex flex-wrap items-center gap-1.5 sm:gap-2">
                        {row.isReactNode ? (
                          row.answer
                        ) : (
                          <span>{row.answer}</span>
                        )}
                        {canPreviewImage && (
                          <button
                            type="button"
                            onClick={() =>
                              openPreview(
                                imagePreviewUrl,
                                imageAttachment?.mime_type || 'image/jpeg',
                              )
                            }
                            className="px-1.5 py-1 rounded-md hover:bg-gray-100 text-gray-500 hover:text-primary transition-colors inline-flex items-center gap-1 shrink-0 text-[10px] font-semibold"
                            title="Preview media"
                            aria-label="Preview media"
                          >
                            <EyeIcon className="w-4 h-4" />
                            <span>Preview</span>
                          </button>
                        )}
                        {!canPreviewImage && hasInlineAttachment && (
                          <button
                            type="button"
                            onClick={() => {
                              const url = getMediaUrl(
                                `/media/whatsapp/attachments/${encodeURIComponent(row.answer)}`,
                              );
                              openPreview(url, '');
                            }}
                            className="p-1 rounded-md hover:bg-gray-100 text-gray-500 hover:text-primary transition-colors inline-flex shrink-0"
                            title="Preview Attachment"
                          >
                            <EyeIcon className="w-4 h-4" />
                          </button>
                        )}
                        {!canPreviewImage &&
                          row.attachments?.length > 0 &&
                          row.attachments.map((att, idx) => {
                            const url = getMediaUrl(att?.file);
                            if (!url) return null;
                            return (
                              <button
                                key={att.id || `${row.key}-att-${idx}`}
                                type="button"
                                onClick={() => openPreview(url, att.mime_type)}
                                className="p-1 rounded-md hover:bg-gray-100 text-gray-500 hover:text-primary transition-colors inline-flex shrink-0"
                                title="Preview Media"
                              >
                                <EyeIcon className="w-4 h-4" />
                              </button>
                            );
                          })}
                      </div>
                    </td>
                  </DashboardTableRow>
                );
              })
            ) : (
              <DashboardTableRow className="!cursor-default">
                <td
                  colSpan={3}
                  className="py-10 text-center text-sm text-[#868686] whitespace-normal px-4"
                >
                  No progress events logged for this session.
                </td>
              </DashboardTableRow>
            )}
          </DashboardTableBody>
        </DashboardTable>
      </div>

      <MediaPreviewModal
        isOpen={!!previewMedia}
        onClose={() => setPreviewMedia(null)}
        mediaUrl={previewMedia?.url}
        mimeType={previewMedia?.mimeType}
      />
    </div>
  );
}

function CRMQuotationsTab({ companyId, clientId }) {
  const { data, isLoading, error } = useWhatsAppContactDetails(
    companyId,
    clientId,
  );

  if (isLoading) {
    const pulse = 'bg-[#EBF4EA] animate-pulse rounded-2xl';
    return (
      <div className="flex flex-col gap-5 w-full">
        <div className="h-6 w-32 bg-[#EBF4EA] animate-pulse rounded-md" />

        <div className={`h-[46px] w-full ${pulse} rounded-[12px]`} />

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-4">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className={`h-[240px] w-full ${pulse}`} />
          ))}
        </div>
      </div>
    );
  }

  if (error || !data) {
    const errorDetail =
      error?.response?.data?.detail ||
      error?.response?.data?.message ||
      error?.message ||
      'Failed to load quotations';
    return (
      <div className="flex flex-col items-center justify-center text-center py-12 px-4 bg-red-500/5 rounded-2xl border border-red-500/10">
        <p className="text-text-primary font-semibold text-sm mb-1">
          Failed to load quotations
        </p>
        <p className="text-red-400 text-xs font-medium max-w-sm">
          {errorDetail}
        </p>
      </div>
    );
  }

  return (
    <div className="w-full">
      <ContactQuotationsList
        quotations={data.quotations}
        title="Quotation history"
        emptyMessage="No quotations generated for this contact yet."
        showSummary
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Live Chat Tab — embeds ChatArea scoped to this lead's WhatsApp conversation
// ---------------------------------------------------------------------------
function CRMLiveChatTab({ companyId, contactDetails, canCreate = false }) {
  const [messages, setMessages] = useState([]);
  const [reactions, setReactions] = useState({});

  // phone_normalized from Lead Details, with leading '+' stripped for Conversation search.
  // Null when missing — search is skipped until lead details provide a value.
  const leadPhone = getConversationSearchPhone(contactDetails);

  // Find the conversation for this lead via Conversation API `search`.
  // Pass null companyId when no phone is available — the hook's `enabled: !!companyId`
  // guard will skip the request automatically.
  const { data: conversationsData, isLoading: isLoadingConversation } =
    useWhatsAppConversations(
      leadPhone ? companyId : null,
      leadPhone ? { search: leadPhone } : {},
    );

  // Pick the first matching conversation
  const conversationRaw =
    conversationsData?.results?.[0] ||
    conversationsData?.pages?.[0]?.results?.[0] ||
    null;

  const activeConversation = conversationRaw
    ? {
        id: conversationRaw.id?.toString(),
        contactName: conversationRaw.customer_name,
        contactPhone: conversationRaw.customer_phone,
        lastMessage: conversationRaw.last_message_preview || '',
        isWindowActive: conversationRaw.is_window_active,
        chatMode: conversationRaw.chat_mode || 'bot',
        avatarUrl: null,
      }
    : null;

  const conversationId = activeConversation?.id;

  // Load messages with polling
  const {
    messages: rawMessages,
    isLoadingInitial: isLoadingMessages,
    isFetchingOlder,
    fetchOlderMessages,
    fetchNewerMessages: refetch,
    hasOlder,
  } = useCustomWhatsAppMessages(companyId, conversationId);

  // Fetch live window status
  const { data: conversationDetail } = useWhatsAppConversationDetail(
    companyId,
    conversationId,
  );

  const isWindowActive = conversationDetail
    ? conversationDetail.is_window_active
    : (activeConversation?.isWindowActive ?? true);

  const windowExpiresAt = conversationDetail?.window_expires_at ?? null;

  // Send message mutation
  const sendMessage = useSendWhatsAppMessage();

  // React to message mutation
  const reactToMessage = useReactToWhatsAppMessage();

  // Group raw messages into date-labeled UI groups
  useEffect(() => {
    setMessages(groupMessagesByDate(rawMessages, reactions));
  }, [rawMessages, reactions]);

  const handleReactToMessage = (messageId, emoji) => {
    if (!companyId || !canCreate) return;
    // Optimistic toggle
    setReactions((prev) => {
      const current = prev[messageId] || [];
      const updated = current.includes(emoji)
        ? current.filter((e) => e !== emoji)
        : [...current, emoji];
      return { ...prev, [messageId]: updated };
    });
    reactToMessage.mutate(
      { companyId, messageId, emoji },
      {
        onSuccess: (data) => {
          setReactions((prev) => ({
            ...prev,
            [messageId]: data.reaction || [],
          }));
        },
        onError: () => {
          toast.error('Failed to update reaction');
          refetch();
        },
      },
    );
  };

  const handleSendMessage = async (text, file) => {
    if (!canCreate) {
      toast.error('You do not have permission to send messages.');
      return;
    }
    if (!text?.trim() && !file) return;
    if (!activeConversation || !companyId) return;

    if (!isWindowActive) {
      toast.error(
        'This WhatsApp conversation window is expired. You cannot send a new message.',
      );
      return;
    }

    if (
      file &&
      (file.name?.toLowerCase().endsWith('.heic') ||
        file.name?.toLowerCase().endsWith('.heif'))
    ) {
      const loadingToastId = toast.loading('Converting HEIC image...');
      try {
        const convertedBlob = await heic2any({
          blob: file,
          toType: 'image/jpeg',
          quality: 0.8,
        });
        const blob = Array.isArray(convertedBlob)
          ? convertedBlob[0]
          : convertedBlob;
        file = new File([blob], file.name.replace(/\.hei(c|f)$/i, '.jpeg'), {
          type: 'image/jpeg',
        });
        toast.dismiss(loadingToastId);
      } catch {
        toast.dismiss(loadingToastId);
        toast.error('Failed to convert HEIC image');
        return;
      }
    }

    if (file) {
      const fileExt = file.name
        .substring(file.name.lastIndexOf('.'))
        .toLowerCase();
      const isVideoFile =
        file.type?.startsWith('video/') ||
        ['.mp4', '.3gp', '.mov', '.avi', '.mkv', '.webm'].includes(fileExt);
      if (isVideoFile && file.size > 16 * 1024 * 1024) {
        toast.error('Video size exceeds 16MB limit');
        return;
      }
    }

    const isAudio =
      file?.type?.startsWith('audio/') || file?.name?.endsWith('.ogg');
    const isImage = file?.type?.startsWith('image/');
    const isVideo = file?.type?.startsWith('video/');

    let messageType = 'text';
    if (isAudio) messageType = 'audio';
    else if (isImage) messageType = 'image';
    else if (isVideo) messageType = 'video';
    else if (file) messageType = 'document';

    const newMessageId = Date.now().toString();
    const newMessage = {
      id: newMessageId,
      content: text || '',
      direction: 'outbound',
      time: 'Just now',
      status: 'sending',
      messageType,
      mediaUrl: file ? URL.createObjectURL(file) : null,
      fileName: file ? file.name : null,
      mimeType: file ? file.type : null,
    };

    // Optimistic UI update
    setMessages((prevGroups) => {
      const todayStr = 'Today';
      if (prevGroups.length === 0) {
        return [{ date: todayStr, messages: [newMessage] }];
      }
      const lastGroup = prevGroups[prevGroups.length - 1];
      if (lastGroup.date === todayStr) {
        return [
          ...prevGroups.slice(0, -1),
          { ...lastGroup, messages: [...lastGroup.messages, newMessage] },
        ];
      }
      return [...prevGroups, { date: todayStr, messages: [newMessage] }];
    });

    let uploadToastId = null;
    if (file) uploadToastId = toast.loading('Sending media...');

    let payload;
    if (file) {
      payload = new FormData();
      payload.append('to_phone', activeConversation.contactPhone);
      if (text) payload.append('text', text);
      payload.append('media_file', file);
      payload.append('message_type', messageType);
    } else {
      payload = { to_phone: activeConversation.contactPhone, text };
    }

    sendMessage.mutate(
      { companyId, payload },
      {
        onSuccess: () => {
          if (uploadToastId) toast.dismiss(uploadToastId);
          refetch();
        },
        onError: () => {
          if (uploadToastId) {
            toast.dismiss(uploadToastId);
            toast.error('Failed to send media');
          }
          setMessages((prevGroups) =>
            prevGroups.map((group) => ({
              ...group,
              messages: group.messages.map((m) =>
                m.id === newMessageId ? { ...m, status: 'failed' } : m,
              ),
            })),
          );
        },
      },
    );
  };

  // Loading state while finding the conversation
  if (isLoadingConversation || (!conversationRaw && leadPhone)) {
    if (isLoadingConversation) {
      return (
        <div className="flex flex-col items-center justify-center h-64 gap-3">
          <div className="w-8 h-8 border-[3px] border-[#50AB3D]/30 border-t-[#50AB3D] rounded-full animate-spin" />
          <p className="text-sm text-[#868686]">Loading chat...</p>
        </div>
      );
    }
  }

  // No conversation found
  if (!leadPhone || !conversationRaw) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-3 text-center px-6">
        <div className="w-14 h-14 rounded-full bg-[#EBF4EA] flex items-center justify-center">
          <svg
            className="w-7 h-7 text-[#50AB3D]"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
            />
          </svg>
        </div>
        <p className="text-sm font-semibold text-[#1F2241]">
          No WhatsApp conversation found
        </p>
        <p className="text-xs text-[#868686] max-w-xs">
          {!leadPhone
            ? 'This lead does not have a phone number assigned.'
            : 'No WhatsApp conversation has been started with this lead yet.'}
        </p>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col rounded-[16px] overflow-hidden border border-[#EBF4EA]">
      <ChatArea
        activeConversation={activeConversation}
        messages={messages}
        isLoading={isLoadingMessages}
        hasNextPage={hasOlder}
        isFetchingNextPage={isFetchingOlder}
        fetchNextPage={fetchOlderMessages}
        isWindowActive={isWindowActive}
        windowExpiresAt={windowExpiresAt}
        onSendMessage={handleSendMessage}
        onReactToMessage={handleReactToMessage}
        canSend={canCreate}
        canReact={canCreate}
      />
    </div>
  );
}

function renderDictionary(data, { pageStyle = false } = {}) {
  if (!data) return null;
  let parsedData = data;
  if (typeof data === 'string') {
    try {
      parsedData = JSON.parse(data);
    } catch {
      return (
        <p
          className={`text-sm break-words ${pageStyle ? 'text-[#1F2241]' : 'text-text-primary'}`}
        >
          {data}
        </p>
      );
    }
  }
  if (
    typeof parsedData !== 'object' ||
    Array.isArray(parsedData) ||
    parsedData === null
  ) {
    return (
      <p
        className={`text-sm break-words ${pageStyle ? 'text-[#1F2241]' : 'text-text-primary'}`}
      >
        {String(parsedData)}
      </p>
    );
  }
  const keys = Object.keys(parsedData);
  if (keys.length === 0) {
    return (
      <p
        className={`text-sm italic ${pageStyle ? 'text-[#868686]' : 'text-text-muted'}`}
      >
        Empty
      </p>
    );
  }

  if (pageStyle) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {keys.map((key) => (
          <div key={key} className="min-w-0">
            <p className="text-[12px] text-[#868686] mb-1 font-medium capitalize">
              {key.replace(/_/g, ' ')}
            </p>
            <p className="text-sm text-[#1F2241] font-medium break-words">
              {typeof parsedData[key] === 'object'
                ? JSON.stringify(parsedData[key])
                : String(parsedData[key])}
            </p>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2">
      {keys.map((key) => (
        <div
          key={key}
          className="bg-input-background p-3 rounded-xl border border-border-default"
        >
          <p className="text-xs text-text-muted uppercase tracking-wider mb-1 font-medium">
            {key.replace(/_/g, ' ')}
          </p>
          <p className="text-sm text-text-primary font-medium break-words">
            {typeof parsedData[key] === 'object'
              ? JSON.stringify(parsedData[key])
              : String(parsedData[key])}
          </p>
        </div>
      ))}
    </div>
  );
}

function PageOverviewSkeleton() {
  const bar = 'bg-[#EBF4EA] animate-pulse rounded';
  return (
    <div className="grid grid-cols-1 lg:grid-cols-[340px_1fr] gap-6 items-start">
      {/* LEFT COLUMN */}
      <div className="bg-[#F5F5F5] rounded-[16px] p-4 sm:p-5 flex flex-col gap-6">
        <div className="flex flex-col gap-3">
          <div className={`h-4 w-28 ${bar}`} />
          <div className="bg-white rounded-[12px] p-4 flex flex-col gap-3.5">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="flex items-center justify-between gap-3">
                <div className={`h-3.5 w-16 ${bar}`} />
                <div className={`h-3.5 w-24 ${bar}`} />
              </div>
            ))}
          </div>
        </div>
        <div className="flex flex-col gap-3">
          <div className={`h-4 w-16 ${bar}`} />
          <div className="bg-white rounded-[12px] p-4 flex flex-wrap gap-2">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className={`h-7 w-20 rounded-full ${bar}`} />
            ))}
          </div>
        </div>
        <div className="flex flex-col gap-3">
          <div className={`h-4 w-20 ${bar}`} />
          <div className="bg-white rounded-[12px] p-4 flex flex-col gap-3">
            {Array.from({ length: 2 }).map((_, i) => (
              <div key={i} className="flex items-center gap-2.5">
                <div className={`h-5 w-5 rounded-full ${bar}`} />
                <div className={`h-3.5 w-24 ${bar}`} />
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* RIGHT COLUMN */}
      <div className="flex flex-col gap-6">
        <div className="flex flex-col gap-3">
          <div className={`h-4 w-20 ${bar}`} />
          <div className="bg-[#F5F5F5] rounded-[16px] p-4 sm:p-5">
            <div className="bg-white rounded-[12px] p-5 flex flex-col gap-4">
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="flex gap-4">
                  <div className={`h-10 w-10 rounded-full shrink-0 ${bar}`} />
                  <div className="flex-1 flex flex-col gap-2">
                    <div className={`h-3.5 w-32 ${bar}`} />
                    <div className={`h-3.5 w-full max-w-md ${bar}`} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="flex flex-col gap-3">
          <div className={`h-4 w-32 ${bar}`} />
          <div className="bg-[#F5F5F5] rounded-[16px] p-4 sm:p-5">
            <div className="bg-white rounded-[12px] p-4 grid grid-cols-2 gap-3">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="flex flex-col gap-1.5">
                  <div className={`h-3 w-16 ${bar}`} />
                  <div className={`h-3.5 w-24 ${bar}`} />
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="flex flex-col gap-3">
          <div className={`h-4 w-24 ${bar}`} />
          <div className="bg-[#F5F5F5] rounded-[16px] p-4 sm:p-5">
            <div className="bg-white rounded-[12px] border border-[#EBF4EA] overflow-hidden">
              <div className={`h-12 w-full ${bar} !rounded-none`} />
              {Array.from({ length: 3 }).map((_, i) => (
                <div
                  key={i}
                  className="h-12 w-full flex items-center px-4 border-t border-[#EBF4EA]"
                >
                  <div className={`h-3.5 w-full max-w-xs ${bar}`} />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function QuickNoteComposer({
  companyId,
  contactId,
  contactName,
  canCreate,
  draft,
  setDraft,
  expanded,
  setExpanded,
}) {
  const queryClient = useQueryClient();
  const [error, setError] = useState('');
  const [focusNonce, setFocusNonce] = useState(0);
  const trimmed = draft.trim();

  const openComposer = () => {
    setExpanded(true);
    setFocusNonce((n) => n + 1);
  };

  useEffect(() => {
    if (!expanded || !trimmed) return undefined;
    const onLeave = (event) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', onLeave);
    return () => window.removeEventListener('beforeunload', onLeave);
  }, [expanded, trimmed]);

  const { mutate, isPending } = useMutation({
    mutationFn: () => {
      if (!canCreate) throw new Error('Permission denied.');
      return createCRMContactNote({
        companyId,
        contactId,
        payload: { body: trimmed },
      });
    },
    onSuccess: () => {
      toast.success('Note added successfully!');
      setDraft('');
      setExpanded(false);
      setError('');
      queryClient.invalidateQueries({
        queryKey: ['crm-contact-notes', companyId, contactId],
      });
      queryClient.invalidateQueries({
        queryKey: ['crm-contact-timeline', companyId, contactId],
      });
    },
    onError: (err) => {
      const { message } = parseApiError(err);
      setError(message || 'Failed to add note.');
    },
  });

  const requestClose = useCallback(() => {
    if (isPending) return;
    if (trimmed && !window.confirm('Discard this unsaved note?')) return;
    setDraft('');
    setError('');
    setExpanded(false);
  }, [isPending, trimmed, setDraft, setExpanded]);

  useEffect(() => {
    if (!expanded) return undefined;
    const onKey = (event) => {
      if (event.key !== 'Escape') return;
      requestClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [expanded, requestClose]);

  useEffect(() => {
    if (!expanded) return undefined;
    const id = window.setTimeout(() => {
      document.getElementById('overview-quick-note')?.focus();
    }, 0);
    return () => window.clearTimeout(id);
  }, [expanded, focusNonce]);

  if (!canCreate) return null;

  return (
    <div className="min-w-0">
      <button
        type="button"
        onClick={openComposer}
        className="flex h-[120px] w-full items-start rounded-[10px] border border-[#F4E7A8] bg-[#FFFBE6] p-3 text-left text-sm text-[#868686]"
      >
        Add a note…
      </button>
      {expanded &&
        createPortal(
          <div
            className="fixed inset-x-0 bottom-0 z-[80] flex max-h-[100dvh] w-full flex-col overflow-hidden rounded-t-2xl border border-[#EBF4EA] bg-white shadow-2xl sm:inset-x-auto sm:bottom-4 sm:right-4 sm:max-h-[min(80vh,640px)] sm:w-[520px] sm:rounded-2xl"
            role="dialog"
            aria-label="Add Note"
          >
            <div className="flex shrink-0 items-center justify-between gap-2 border-b border-[#EBF4EA] px-4 py-3">
              <h2 className="text-base font-semibold text-[#1F2241]">
                Add Note
              </h2>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  className="flex h-8 w-8 items-center justify-center rounded-lg text-[#1F2241] hover:bg-[#F5F5F5]"
                  aria-label="Close"
                  disabled={isPending}
                  onClick={requestClose}
                >
                  <span className="text-lg leading-none">×</span>
                </button>
              </div>
            </div>
            <p className="shrink-0 px-4 pt-2 text-sm text-[#868686]">
              Related to:{' '}
              <span className="font-medium text-[#1F2241]">
                {contactName || 'Current contact'}
              </span>
            </p>
            <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3">
              <textarea
                id="overview-quick-note"
                value={draft}
                onChange={(e) => {
                  setDraft(e.target.value);
                  if (error) setError('');
                }}
                disabled={isPending}
                placeholder="Write a note…"
                className="min-h-[220px] w-full resize-none rounded-[10px] border border-[#EBF4EA] px-3 py-2 text-sm text-[#1F2241] placeholder:text-[#868686] focus:outline-none focus:ring-1 focus:ring-[#50AB3D]/40"
              />
              {error && (
                <p className="mt-2 text-sm text-red-600" role="alert">
                  {error}
                </p>
              )}
            </div>
            <div className="flex shrink-0 justify-end gap-2 border-t border-[#EBF4EA] bg-white px-4 py-3">
              <Button
                type="button"
                variant="secondary"
                className="h-10 px-4"
                disabled={isPending}
                onClick={requestClose}
              >
                Cancel
              </Button>
              <Button
                type="button"
                variant="primary"
                className="h-10 px-4"
                disabled={isPending || !trimmed}
                onClick={() => {
                  if (isPending || !trimmed) return;
                  mutate();
                }}
              >
                {isPending ? 'Saving...' : 'Save Note'}
              </Button>
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
}

function PageOverviewTab({
  contactDetails,
  companyId,
  contactId,
  contactSources,
  isContactSourcesLoading,
  contactTimeline,
  isContactTimelineLoading,
  fetchNextTimelinePage,
  hasNextTimelinePage,
  isFetchingNextTimelinePage,
  onViewAllFollowUps,
  readOnly,
  canCreateLeadManagement = false,
  canUpdateLeadManagement = false,
  canDeleteLeadManagement = false,
  noteDraft,
  setNoteDraft,
  noteExpanded,
  setNoteExpanded,
}) {
  const queryClient = useQueryClient();
  const { can } = usePermissions();
  const canMoveLeads =
    can(PERMISSIONS.PIPELINES.CREATE) || can(PERMISSIONS.PIPELINES.UPDATE);
  const [pendingLostMove, setPendingLostMove] = useState(null);
  const [optimisticStageId, setOptimisticStageId] = useState(null);
  const { formatCurrency } = useFormatters();

  const { data: stagesData } = useQuery({
    queryKey: ['crm-pipeline-stages', companyId],
    queryFn: async () => {
      const res = await getCRMPipelineStages(companyId);
      return res?.results || res || [];
    },
    enabled: !!companyId,
  });
  const pipelineStages = Array.isArray(stagesData) ? stagesData : [];

  // Contact detail often omits current_stage — resolve from pipeline board.
  const boardSearch =
    contactDetails?.phone ||
    contactDetails?.name ||
    contactDetails?.email ||
    '';
  const { data: boardData } = useQuery({
    queryKey: [
      'crm-pipeline-board',
      companyId,
      'contact-stage-lookup',
      contactId,
      boardSearch,
    ],
    queryFn: async () => {
      const fetchBoard = async (params = {}) => {
        const res = await getCRMPipelineBoard(companyId, params);
        return res?.results || res || [];
      };

      const searched = await fetchBoard(
        boardSearch ? { search: boardSearch } : {},
      );
      if (findContactStageFromBoard(searched, contactId)) return searched;

      // Search can miss the lead — fall back to unfiltered board once.
      if (boardSearch) {
        return fetchBoard();
      }
      return searched;
    },
    enabled: !!companyId && !!contactId,
    staleTime: 30_000,
  });
  const boardStage = findContactStageFromBoard(boardData, contactId);

  const resolvedStageId = resolveContactStageId(
    contactDetails,
    pipelineStages,
    boardStage,
  );
  const currentStageId =
    optimisticStageId != null ? optimisticStageId : resolvedStageId;
  const selectedStage =
    matchStageByNameOrId(pipelineStages, currentStageId) ||
    (boardStage && String(boardStage.id) === String(currentStageId)
      ? boardStage
      : null) ||
    (typeof contactDetails.current_stage === 'object'
      ? contactDetails.current_stage
      : null) ||
    (typeof contactDetails.stage === 'object' ? contactDetails.stage : null);
  const stageColor = selectedStage?.color || '#3b82f6';
  const stageLabel = selectedStage?.name || 'Select stage';

  useEffect(() => {
    setOptimisticStageId(null);
  }, [contactId]);

  useEffect(() => {
    if (
      optimisticStageId != null &&
      resolvedStageId != null &&
      String(optimisticStageId) === String(resolvedStageId)
    ) {
      setOptimisticStageId(null);
    }
  }, [optimisticStageId, resolvedStageId]);

  const moveStageMutation = useMutation({
    mutationFn: (args) => {
      if (!canMoveLeads) {
        throw new Error('Permission denied: Cannot move leads.');
      }
      return moveCRMContactStage(args);
    },
    onMutate: async ({ payload }) => {
      const previousStageId = currentStageId;
      setOptimisticStageId(payload.target_stage_id);
      return { previousStageId };
    },
    onSuccess: (_data, variables) => {
      setPendingLostMove(null);
      toast.success('Lead moved successfully!');
      queryClient.invalidateQueries({
        queryKey: ['crm-contact-detail', companyId, contactId],
      });
      queryClient.invalidateQueries({
        queryKey: ['crm-contact-timeline', companyId, contactId],
      });
      queryClient.invalidateQueries({
        queryKey: ['crm-contacts'],
      });
      queryClient.invalidateQueries({
        queryKey: ['crm-pipeline-board'],
      });
      syncPipelineBoardContact(queryClient, contactId, {
        target_stage_id: variables?.payload?.target_stage_id,
        lost_reason: variables?.payload?.lost_reason,
      });
    },
    onError: (err, _vars, context) => {
      setOptimisticStageId(context?.previousStageId ?? null);
      const status = err?.response?.status;
      if (status >= 500) {
        toast.error('An unexpected server error occurred. Please try again.');
      } else {
        const { message } = parseApiError(err);
        toast.error(message || 'Failed to move lead.');
      }
    },
  });

  const handleStageChange = (newStageId) => {
    if (String(newStageId) === String(currentStageId)) return;
    const targetStage = pipelineStages.find(
      (s) => String(s.id) === String(newStageId),
    );
    if (!targetStage) return;
    if (targetStage.is_lost || targetStage.slug === 'lost') {
      setPendingLostMove({
        contactId,
        target_stage_id: targetStage.id,
      });
      return;
    }
    moveStageMutation.mutate({
      companyId,
      contactId,
      payload: { target_stage_id: newStageId },
    });
  };

  const personalInfoRows = [
    { label: 'Email', value: contactDetails.email },
    { label: 'Phone Number', value: getDisplayPhone(contactDetails) },
    { label: 'Created', value: formatDate(contactDetails.created_at) },
    {
      label: 'Estimated Value',
      value:
        contactDetails.estimated_value !== null &&
        contactDetails.estimated_value !== undefined
          ? formatCurrency(contactDetails.estimated_value)
          : '-',
    },
  ];

  const assignedEmployeeId =
    contactDetails.assigned_to_employee_id ||
    contactDetails.assigned_to_id ||
    contactDetails.assignee_id ||
    '';
  const assignedEmployeeName =
    contactDetails.assigned_to_employee_name ||
    (contactDetails.assigned_to_name &&
    contactDetails.assigned_to_name !== 'Unassigned'
      ? contactDetails.assigned_to_name
      : null) ||
    '';
  const lostBy =
    contactDetails.lost_by_name || contactDetails.lost_by?.name || '-';
  const lostReason =
    typeof contactDetails.lost_reason === 'object'
      ? contactDetails.lost_reason?.name ||
        contactDetails.lost_reason?.reason ||
        '-'
      : contactDetails.lost_reason || '-';
  const lostInfoRows = [
    { label: 'Lost By', value: lostBy },
    { label: 'Lost At', value: formatDate(contactDetails.lost_at) },
    { label: 'Reason', value: lostReason },
  ];
  const hasLostInfo = Boolean(
    contactDetails.lost_by_name ||
    contactDetails.lost_by?.name ||
    contactDetails.lost_at ||
    contactDetails.lost_reason,
  );

  return (
    <div className="overview-grid grid grid-cols-1 lg:grid-cols-[340px_1fr] gap-[10px] items-start">
      {/* LEFT COLUMN */}
      <div className="bg-[#F5F5F5] rounded-[16px] p-[14px] sm:p-[14px] flex flex-col gap-[14px] min-w-0 lg:sticky lg:top-0">
        {/* Personal Info */}
        <div className="flex flex-col gap-3">
          <h3 className="text-[18px] font-medium text-[#50AB3D]">
            Personal Info
          </h3>
          <div className="personal-info bg-white rounded-[12px] p-4 flex flex-col gap-3.5">
            {personalInfoRows.map(({ label, value }) => (
              <div
                key={label}
                className="flex items-center justify-between gap-3"
              >
                <span className="text-sm text-[#868686] shrink-0">{label}</span>
                <span
                  className="personal-info-value text-sm font-semibold text-[#1F2241] text-right break-words min-w-0"
                  title={value || '-'}
                >
                  {value || '-'}
                </span>
              </div>
            ))}
            <div className="personal-info-assign flex flex-col gap-2">
              <span className="text-sm text-[#868686]">Assigned To</span>
              {canUpdateLeadManagement && !readOnly ? (
                <InlineEmployeeSelector
                  companyId={companyId}
                  contactId={contactId}
                  employeeId={assignedEmployeeId}
                  employeeName={assignedEmployeeName}
                />
              ) : (
                <span className="text-sm font-semibold text-[#1F2241]">
                  {assignedEmployeeName || 'Unassigned'}
                </span>
              )}
            </div>
            {pipelineStages.length > 0 && (
              <div className="flex items-center justify-between gap-3">
                <span className="text-sm text-[#868686]">Stage</span>
                {canMoveLeads && !readOnly ? (
                  <div
                    className="shrink-0"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <CustomSelect
                      value={currentStageId ?? ''}
                      onChange={handleStageChange}
                      options={pipelineStages.map((s) => ({
                        value: s.id,
                        label: s.name,
                      }))}
                      placeholder={stageLabel}
                      disabled={moveStageMutation.isPending}
                      hideCheckIcon
                      hideChevron
                      dropdownWidth={180}
                      buttonClassName={`${TABLE_CHIP_BASE} !py-0.5 !px-2.5 !min-h-[26px] max-w-[150px] appearance-none cursor-pointer focus:outline-none`}
                      buttonStyle={{
                        backgroundColor: `${stageColor}1A`,
                        color: stageColor,
                        borderColor: stageColor,
                      }}
                    />
                  </div>
                ) : (
                  <span
                    className={`${TABLE_CHIP_BASE} !py-0.5 !px-2.5 !min-h-[26px] max-w-[150px] truncate`}
                    style={{
                      backgroundColor: `${stageColor}1A`,
                      color: stageColor,
                      borderColor: stageColor,
                    }}
                  >
                    {stageLabel}
                  </span>
                )}
              </div>
            )}
          </div>
        </div>

        <QuickNoteComposer
          companyId={companyId}
          contactId={contactId}
          canCreate={canCreateLeadManagement && !readOnly}
          contactName={
            readAnyContactName(contactDetails) ||
            getDisplayPhone(contactDetails)
          }
          draft={noteDraft}
          setDraft={setNoteDraft}
          expanded={noteExpanded}
          setExpanded={setNoteExpanded}
        />

        {/* Tags */}
        <div className="flex flex-col gap-3">
          <h3 className="text-[18px] font-medium text-[#50AB3D]">Tags</h3>
          <div className="bg-white rounded-[12px] p-4">
            <InlineTagSelector
              companyId={companyId}
              contactId={contactId}
              contactTags={contactDetails?.tags}
              disabled={!canUpdateLeadManagement || readOnly}
              appearance="compact"
            />
          </div>
        </div>

        {/* Sources */}
        <div className="flex flex-col gap-3">
          <h3 className="text-[18px] font-medium text-[#50AB3D]">Sources</h3>
          <div className="bg-white rounded-[12px] p-4">
            {isContactSourcesLoading ? (
              <div className="flex justify-center py-4">
                <div className="w-5 h-5 border-2 border-[#50AB3D]/30 border-t-[#50AB3D] rounded-full animate-spin" />
              </div>
            ) : contactSources?.length > 0 ? (
              <div className="flex flex-col gap-3">
                {contactSources.map((source, idx) => (
                  <div
                    key={idx}
                    className="flex items-center justify-between gap-3"
                  >
                    <div className="flex items-center gap-2.5 min-w-0">
                      <SourceIcon type={source.source_type} />
                      <span className="text-sm font-medium text-[#1F2241] capitalize truncate">
                        {source.source_type || 'Unknown Source'}
                      </span>
                    </div>
                    {source.is_primary && (
                      <span className="px-2 py-0.5 text-xs font-semibold bg-[#EBF4EA] text-[#50AB3D] rounded shrink-0">
                        Primary
                      </span>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-[#868686] italic">
                No sources found for this contact.
              </p>
            )}
          </div>
        </div>

        {hasLostInfo && (
          <div className="flex flex-col gap-3">
            <h3 className="text-[18px] font-medium text-[#50AB3D]">
              Lost Info
            </h3>
            <div className="flex flex-col gap-3.5 rounded-[12px] bg-white p-4">
              {lostInfoRows.map(({ label, value }) => (
                <div
                  key={label}
                  className="flex items-center justify-between gap-3"
                >
                  <span className="text-sm text-[#868686]">{label}</span>
                  <span className="text-right text-sm font-semibold text-[#1F2241]">
                    {value}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* RIGHT COLUMN */}
      <div className="flex flex-col min-w-0">
        {/* Timeline */}
        <div className="bg-[#F5F5F5] rounded-t-[16px] p-[14px] sm:p-[14px] flex flex-col gap-3">
          <h3 className="text-[18px] font-medium text-[#50AB3D]">Timeline</h3>
          <div className="overview-timeline bg-white rounded-[12px] p-2 sm:p-2">
            <CRMActivityTimeline
              timeline={contactTimeline}
              isLoading={isContactTimelineLoading}
              fetchNextPage={fetchNextTimelinePage}
              hasNextPage={hasNextTimelinePage}
              isFetchingNextPage={isFetchingNextTimelinePage}
              variant="page"
            />
          </div>
        </div>

        {/* Custom Fields */}
        {contactDetails.custom_fields &&
          Object.keys(contactDetails.custom_fields).length > 0 && (
            <div className="bg-[#F5F5F5] p-[14px] sm:p-[14px] flex flex-col gap-3">
              <h3 className="text-[18px] font-medium text-[#50AB3D]">
                Custom Fields
              </h3>
              <div className="bg-white rounded-[12px] p-4">
                {renderDictionary(contactDetails.custom_fields, {
                  pageStyle: true,
                })}
              </div>
            </div>
          )}

        {/* Additional Data */}
        {contactDetails.metadata && (
          <div className="bg-[#F5F5F5] p-[14px] sm:p-[14px] flex flex-col gap-3">
            <h3 className="text-[18px] font-medium text-[#50AB3D]">
              Additional Data
            </h3>
            <div className="bg-white rounded-[12px] p-4 min-h-[100px]">
              {renderDictionary(contactDetails.metadata, { pageStyle: true })}
            </div>
          </div>
        )}

        {/* Follow-Ups */}
        <CRMFollowUpsOverviewTable
          companyId={companyId}
          contactId={contactId}
          readOnly={readOnly}
          canDelete={canDeleteLeadManagement}
          onViewAll={onViewAllFollowUps}
        />
      </div>

      <CRMLostReasonModal
        isOpen={!!pendingLostMove}
        onClose={() => setPendingLostMove(null)}
        onSubmit={(reason) => {
          if (!pendingLostMove) return;
          moveStageMutation.mutate({
            companyId,
            contactId: pendingLostMove.contactId,
            payload: {
              target_stage_id: pendingLostMove.target_stage_id,
              lost_reason: reason,
            },
          });
        }}
        isPending={moveStageMutation.isPending}
      />
    </div>
  );
}

function DrawerOverviewTab({
  contactDetails,
  tagsData,
  showConsentHistory,
  setShowConsentHistory,
  isConsentHistoryLoading,
  consentHistoryData,
  contactSources,
  isContactSourcesLoading,
  safeTagsList,
  safeRender,
}) {
  const tagsArray = Array.isArray(tagsData)
    ? tagsData
    : tagsData?.results || [];

  return (
    <div className="flex flex-col gap-5">
      <div className="bg-input-background rounded-2xl border border-border-default p-4 grid grid-cols-2 gap-x-4 gap-y-5">
        {[
          { label: 'Name', value: contactDetails.name || '-' },
          { label: 'Email', value: contactDetails.email },
          { label: 'Phone', value: getDisplayPhone(contactDetails) },
          { label: 'Created', value: formatDate(contactDetails.created_at) },
          {
            label: 'Assigned To',
            value:
              contactDetails.assigned_to_employee_name ||
              (contactDetails.assigned_to_name &&
              contactDetails.assigned_to_name !== 'Unassigned'
                ? contactDetails.assigned_to_name
                : null) ||
              'Unassigned',
          },
          {
            label: 'Estimated Value',
            value:
              contactDetails.estimated_value !== null &&
              contactDetails.estimated_value !== undefined
                ? String(contactDetails.estimated_value)
                : '-',
          },
        ].map(({ label, value }) => (
          <div key={label}>
            <p className="text-xs text-text-muted uppercase tracking-wider mb-1 font-semibold">
              {label}
            </p>
            <p className="text-sm text-text-primary font-medium">
              {value || '-'}
            </p>
          </div>
        ))}
      </div>

      {(contactDetails.lost_reason || contactDetails.lost_by_name) && (
        <div className="bg-red-500/10 rounded-2xl border border-red-500/20 p-4 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-5">
          <div className="sm:col-span-2">
            <p className="text-xs text-red-400 uppercase tracking-wider mb-1 font-bold">
              Lost Information
            </p>
          </div>
          {contactDetails.lost_by_name && (
            <div>
              <p className="text-xs text-text-muted uppercase tracking-wider mb-1 font-semibold">
                Lost By
              </p>
              <p className="text-sm text-text-primary font-medium">
                {contactDetails.lost_by_name}
              </p>
            </div>
          )}
          {contactDetails.lost_at && (
            <div>
              <p className="text-xs text-text-muted uppercase tracking-wider mb-1 font-semibold">
                Lost At
              </p>
              <p className="text-sm text-text-primary font-medium">
                {formatDate(contactDetails.lost_at)}
              </p>
            </div>
          )}
          {contactDetails.lost_reason && (
            <div className="sm:col-span-2">
              <p className="text-xs text-text-muted uppercase tracking-wider mb-1 font-semibold">
                Reason
              </p>
              <p className="text-sm text-text-primary font-medium">
                {contactDetails.lost_reason}
              </p>
            </div>
          )}
        </div>
      )}

      <div className="bg-input-background rounded-2xl border border-border-default p-4">
        <div className="flex items-center justify-between mb-3">
          <p className="text-xs text-text-muted uppercase tracking-wider font-bold">
            Tags
          </p>
        </div>
        <div className="flex flex-wrap gap-1.5 min-h-[28px]">
          {safeTagsList.length > 0 ? (
            safeTagsList.map((tag) => {
              const tagObj = tagsArray.find((t) => t.name === tag);
              return (
                <span
                  key={tag}
                  className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border ${!tagObj?.color ? 'bg-primary/10 border-primary/40 text-primary' : ''}`}
                  style={
                    tagObj?.color
                      ? {
                          backgroundColor: `${tagObj.color}15`,
                          borderColor: `${tagObj.color}40`,
                          color: tagObj.color,
                        }
                      : undefined
                  }
                >
                  {tag}
                </span>
              );
            })
          ) : (
            <p className="text-sm text-text-muted italic">No tags assigned</p>
          )}
        </div>
      </div>

      <div className="bg-input-background rounded-2xl border border-border-default p-4">
        <div className="flex items-center justify-between mb-3">
          <p className="text-xs text-text-muted uppercase tracking-wider font-bold">
            Consent Status
          </p>
          <button
            type="button"
            onClick={() => setShowConsentHistory((v) => !v)}
            className="text-xs text-text-muted hover:text-text-primary font-semibold"
          >
            {showConsentHistory ? 'Hide History' : 'View History'}
          </button>
        </div>
        <p className="text-sm text-text-primary">
          {safeRender(formatConsentStatus(contactDetails.consent_status))}
        </p>
        {showConsentHistory && (
          <div className="mt-3 bg-layer-1-page border border-border-default rounded-lg p-3">
            <p className="text-xs font-semibold text-text-muted mb-2 uppercase tracking-wider">
              History Log
            </p>
            {isConsentHistoryLoading ? (
              <div className="flex justify-center py-2">
                <div className="w-4 h-4 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
              </div>
            ) : consentHistoryData?.length > 0 ? (
              <ul className="space-y-3">
                {consentHistoryData.map((log) => (
                  <li key={log.id} className="text-sm">
                    <div className="flex justify-between items-start mb-0.5">
                      <span className="font-medium text-text-primary capitalize">
                        {log.status}
                      </span>
                      <span className="text-xs text-text-muted shrink-0 ml-2">
                        {formatDate(log.recorded_at)}
                      </span>
                    </div>
                    {log.recorded_by_name && (
                      <p className="text-xs text-text-muted">
                        By: {log.recorded_by_name}
                      </p>
                    )}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-xs text-text-muted italic">
                No consent history found.
              </p>
            )}
          </div>
        )}
      </div>

      {contactDetails.custom_fields && (
        <div className="bg-input-background rounded-2xl border border-border-default p-4">
          <p className="text-xs text-text-muted uppercase tracking-wider font-bold mb-2">
            Custom Fields
          </p>
          {renderDictionary(contactDetails.custom_fields)}
        </div>
      )}

      {contactDetails.metadata && (
        <div className="bg-input-background rounded-2xl border border-border-default p-4">
          <p className="text-xs text-text-muted uppercase tracking-wider font-bold mb-2">
            Additional Data
          </p>
          {renderDictionary(contactDetails.metadata)}
        </div>
      )}

      <div className="bg-input-background rounded-2xl border border-border-default p-4">
        <p className="text-xs text-text-muted uppercase tracking-wider font-bold mb-3">
          Sources
        </p>
        {isContactSourcesLoading ? (
          <div className="flex justify-center py-4">
            <div className="w-5 h-5 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
          </div>
        ) : contactSources?.length > 0 ? (
          <div className="space-y-3">
            {contactSources.map((source, idx) => (
              <div
                key={idx}
                className="p-3 rounded-xl bg-layer-1-page border border-border-default"
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="flex items-center gap-2 min-w-0">
                    <SourceIcon type={source.source_type} className="w-5 h-5" />
                    <p className="text-sm font-medium text-text-primary capitalize truncate">
                      {source.source_type || 'Unknown Source'}
                    </p>
                  </span>
                  {source.is_primary && (
                    <span className="px-2 py-0.5 text-xs font-semibold bg-primary/10 text-primary rounded">
                      Primary
                    </span>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-y-2 gap-x-4">
                  {[
                    ['Campaign ID', source.campaign_id],
                    ['Campaign Name', source.campaign_name],
                    ['UTM Source', source.utm_source],
                    ['UTM Medium', source.utm_medium],
                    ['UTM Campaign', source.utm_campaign],
                  ]
                    .filter(([, v]) => v)
                    .map(([label, val]) => (
                      <div key={label}>
                        <p className="text-xs text-text-muted mb-0.5">
                          {label}
                        </p>
                        <p className="text-sm text-text-secondary">{val}</p>
                      </div>
                    ))}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-text-muted">
            No sources found for this contact.
          </p>
        )}
      </div>
    </div>
  );
}

/**
 * Shared contact detail tab body used by Overview page and Pipeline drawer.
 * @param {'page'|'drawer'} variant
 */
export function CRMContactDetailPanel({
  variant = 'drawer',
  activeTab = 'overview',
  contactToView,
  contactDetails,
  isContactDetailsLoading,
  companyId,
  tagsData,
  safeTagsList = [],
  showConsentHistory,
  setShowConsentHistory,
  isConsentHistoryLoading,
  consentHistoryData,
  safeRender = (v) => (v == null ? '-' : String(v)),
  contactSources,
  isContactSourcesLoading,
  contactTimeline,
  isContactTimelineLoading,
  fetchNextTimelinePage,
  hasNextTimelinePage,
  isFetchingNextTimelinePage,
  onViewAllFollowUps,
  onFollowUpCreatingChange,
}) {
  const { can } = usePermissions();
  const canViewLeadManagement = can(PERMISSIONS.LEAD_MANAGEMENT.VIEW);
  const canCreateLeadManagement = can(PERMISSIONS.LEAD_MANAGEMENT.CREATE);
  const canUpdateLeadManagement = can(PERMISSIONS.LEAD_MANAGEMENT.UPDATE);
  const canDeleteLeadManagement = can(PERMISSIONS.LEAD_MANAGEMENT.DELETE);
  const [noteDraft, setNoteDraft] = useState('');
  const [noteExpanded, setNoteExpanded] = useState(false);

  const canAccessLiveChat =
    can(PERMISSIONS.WA_CHATS.CREATE) || can(PERMISSIONS.WA_CHATS.VIEW);
  const canCreateLiveChat = can(PERMISSIONS.WA_CHATS.CREATE);
  const canViewQuotations = can(PERMISSIONS.QUOTATIONS.VIEW);

  const isManagementTab = [
    'notes',
    'tasks',
    'follow-ups',
    'attachments',
    'session',
    'whatsapp',
  ].includes(activeTab);

  const contactId = String(contactDetails?.id || contactToView?.id || '');

  useEffect(() => {
    setNoteDraft('');
    setNoteExpanded(false);
  }, [contactId]);

  if (activeTab === 'live-chat' && !canAccessLiveChat) {
    return null;
  }

  if (activeTab === 'quotations' && !canViewQuotations) {
    return null;
  }

  if (isManagementTab && !canViewLeadManagement) {
    return null;
  }

  if (isContactDetailsLoading) {
    return <PageOverviewSkeleton />;
  }

  if (!contactDetails) {
    return (
      <div className="py-10 text-center text-text-muted">
        Failed to load details
      </div>
    );
  }

  return (
    <>
      {activeTab === 'overview' &&
        (variant === 'page' ? (
          <PageOverviewTab
            contactDetails={contactDetails}
            companyId={companyId}
            contactId={contactId}
            contactSources={contactSources}
            isContactSourcesLoading={isContactSourcesLoading}
            contactTimeline={contactTimeline}
            isContactTimelineLoading={isContactTimelineLoading}
            fetchNextTimelinePage={fetchNextTimelinePage}
            hasNextTimelinePage={hasNextTimelinePage}
            isFetchingNextTimelinePage={isFetchingNextTimelinePage}
            onViewAllFollowUps={onViewAllFollowUps}
            readOnly={false}
            canCreateLeadManagement={canCreateLeadManagement}
            canUpdateLeadManagement={canUpdateLeadManagement}
            canDeleteLeadManagement={canDeleteLeadManagement}
            noteDraft={noteDraft}
            setNoteDraft={setNoteDraft}
            noteExpanded={noteExpanded}
            setNoteExpanded={setNoteExpanded}
          />
        ) : (
          <DrawerOverviewTab
            contactDetails={contactDetails}
            tagsData={tagsData}
            showConsentHistory={showConsentHistory}
            setShowConsentHistory={setShowConsentHistory}
            isConsentHistoryLoading={isConsentHistoryLoading}
            consentHistoryData={consentHistoryData}
            contactSources={contactSources}
            isContactSourcesLoading={isContactSourcesLoading}
            safeTagsList={safeTagsList}
            safeRender={safeRender}
          />
        ))}
      {activeTab === 'notes' && (
        <CRMNotesSection
          companyId={companyId}
          contactId={contactId}
          readOnly={false}
          canCreate={canCreateLeadManagement}
          canUpdate={canUpdateLeadManagement}
          canDelete={canDeleteLeadManagement}
        />
      )}
      {activeTab === 'tasks' && (
        <CRMTasksSection
          companyId={companyId}
          contactId={contactId}
          readOnly={false}
          canCreate={canCreateLeadManagement}
          canUpdate={canUpdateLeadManagement}
          canDelete={canDeleteLeadManagement}
        />
      )}
      {activeTab === 'follow-ups' && (
        <CRMFollowUpsSection
          companyId={companyId}
          contactId={contactId}
          contact={contactDetails}
          readOnly={false}
          canCreate={canCreateLeadManagement}
          canUpdate={canUpdateLeadManagement}
          canDelete={canDeleteLeadManagement}
          onCreatingChange={onFollowUpCreatingChange}
        />
      )}
      {activeTab === 'activity' && (
        <CRMActivityTimeline
          timeline={contactTimeline}
          isLoading={isContactTimelineLoading}
          fetchNextPage={fetchNextTimelinePage}
          hasNextPage={hasNextTimelinePage}
          isFetchingNextPage={isFetchingNextTimelinePage}
        />
      )}
      {activeTab === 'attachments' && (
        <CRMAttachmentsSection
          companyId={companyId}
          contactId={contactId}
          readOnly={false}
          canCreate={canCreateLeadManagement}
          canUpdate={canUpdateLeadManagement}
          canDelete={canDeleteLeadManagement}
        />
      )}
      {activeTab === 'session' && (
        <CRMSessionTab companyId={companyId} clientId={contactId} />
      )}
      {activeTab === 'whatsapp' && (
        <CRMSessionTab companyId={companyId} clientId={contactId} />
      )}
      {activeTab === 'quotations' && canViewQuotations && (
        <CRMQuotationsTab companyId={companyId} clientId={contactId} />
      )}
      {activeTab === 'live-chat' && (
        <CRMLiveChatTab
          key={contactId}
          companyId={companyId}
          contactDetails={contactDetails}
          canCreate={canCreateLiveChat}
        />
      )}
    </>
  );
}
